import { createUseQueryHook } from '../createUseQueryHook';
import { STALE_TIMES } from '../reactQueryClientConfig';
import { fetchCart, fetchUserCart } from './cart.api';
import type {
  Cart,
  DeliveryAddressParams,
  FetchCartProps,
  FetchUserCartProps,
} from './cart.types';

const CART_BASE_KEY = 'cart';

export const cartKeys = {
  /** Wrapped in an object so the address can't pass as a `paymentMethod`. */
  detail: (
    uuid: string,
    paymentMethod?: string,
    deliveryAddress?: DeliveryAddressParams
  ): readonly unknown[] => {
    const key: unknown[] = [CART_BASE_KEY, uuid];

    if (paymentMethod) key.push(paymentMethod);
    if (deliveryAddress && Object.keys(deliveryAddress).length) {
      key.push({ delivery_address: deliveryAddress });
    }

    return key;
  },
  userCart: (appMode: string, storeId?: number | string, uuid?: string) =>
    [CART_BASE_KEY, 'user', appMode, storeId ?? null, uuid ?? null] as const,
};

export const useCart = createUseQueryHook<FetchCartProps, Cart>(
  fetchCart,
  ({ uuid, paymentMethod, deliveryAddress }) =>
    cartKeys.detail(uuid, paymentMethod, deliveryAddress),
  ({ uuid }) => !!uuid,
  { staleTime: STALE_TIMES.zero }
);

export const useUserCart = createUseQueryHook<FetchUserCartProps, Cart | null>(
  fetchUserCart,
  ({ appMode, storeId, uuid }) => cartKeys.userCart(appMode, storeId, uuid),
  // Mirrors the backend guard: a non-default app mode requires a store_id, or
  // the request is guaranteed to fail with InvalidQueryError.
  ({ appMode, storeId }) =>
    !!appMode && (appMode === 'default' || storeId != null)
);
